/* ============================================================================
   HalftoneLayer.tsx — renderer halftone canvas 2D pour le glyph set
   `halftone`. Variation déterministe (sans jitter) du StippleLayer :
   un cercle PARFAITEMENT centré par cellule de grille, radius modulé par
   la luminance. Reproduit l'esthétique d'impression typographique 19e
   siècle / pattern TouchDesigner canonique.

   Pourquoi un fichier séparé ? — la signature visuelle diffère radicalement
   du cyanotype (régularité vs chaos), la composition canvas est plus simple
   (un seul dot par cell), et permet d'évoluer les 2 modes indépendamment.

   Combiner avec : background paper + invert OFF pour effet "journal imprimé"
   classique noir sur blanc.
   ============================================================================ */

import { useEffect, useRef } from 'react';
import type { CanvasParams } from '../../lib/canvasStore';

/** Densité map — doit matcher HALFTONE dans lib/ascii/glyphSets.ts */
const HALFTONE_SET = ' ·°∘∙◦○⊙◉●';

/**
 * Radius base proportionnel au fontSize. Max radius ≈ 50% de la cellule
 * pour que les cercles à pleine densité couvrent presque entièrement la
 * cell sans se chevaucher avec les voisins.
 */
const BASE_DOT_RADIUS_RATIO = 0.42;

/**
 * Palette ID → hex. Halftone respecte la palette utilisateur (contrairement
 * au cyanotype qui force bleu/blanc), pour préserver la doctrine grayscale.
 */
function paletteToHex(id: CanvasParams['palette']): string {
  switch (id) {
    case 'mono-ink': return '#0d0d0d';
    case 'silver': return '#b8b8b8';
    case 'graphite': return '#2c2c2c';
    case 'stone': return '#8a8a8a';
    case 'ash': return '#5a5a5a';
    default: return '#0d0d0d';
  }
}

function glyphToDensity(g: string): number {
  const i = HALFTONE_SET.indexOf(g);
  if (i < 0) return 0;
  return i / (HALFTONE_SET.length - 1);
}

interface Props {
  grid: string[][];
  params: CanvasParams;
}

export function HalftoneLayer({ grid, params }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const ctx = cvs.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = cvs.getBoundingClientRect();
    if (cvs.width !== rect.width * dpr || cvs.height !== rect.height * dpr) {
      cvs.width = Math.max(1, Math.round(rect.width * dpr));
      cvs.height = Math.max(1, Math.round(rect.height * dpr));
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, rect.width, rect.height);

    if (grid.length === 0) return;
    const cols = grid[0]?.length ?? 0;
    const rows = grid.length;
    if (cols === 0) return;

    const fs = params.fontSize;
    const charW = fs * 0.6 + params.letterSpacing;
    const lh = fs * params.lineHeight;
    const blockW = cols * charW;
    const blockH = rows * lh;
    const ox = (rect.width - blockW) / 2;
    const oy = (rect.height - blockH) / 2;

    // Radius max = min(charW, lh) * BASE_DOT_RADIUS_RATIO. Garantit que
    // les cercles à densité 1.0 ne se chevauchent JAMAIS avec les voisins
    // (la cell la plus petite définit le plafond).
    const maxR = Math.min(charW, lh) * BASE_DOT_RADIUS_RATIO;

    // Foreground : palette utilisateur (respect doctrine grayscale).
    let fill = paletteToHex(params.palette);
    if (params.bgPalette === 'ink' && params.palette === 'mono-ink') {
      fill = '#ffffff';
    }
    ctx.fillStyle = fill;

    for (let r = 0; r < rows; r++) {
      const row = grid[r]!;
      // Centre vertical de la cellule
      const cy = oy + r * lh + lh / 2;
      for (let c = 0; c < cols; c++) {
        const ch = row[c];
        if (!ch || ch === ' ') continue;
        const density = glyphToDensity(ch);
        if (density <= 0) continue;

        // Centre horizontal de la cellule
        const cx = ox + c * charW + charW / 2;
        // Radius linéaire de 0 à maxR. Optionnellement : courbe gamma
        // pour accentuer le mid-tone (Math.pow(density, 0.7)).
        const radius = maxR * density;

        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }, [
    grid,
    params.fontSize,
    params.letterSpacing,
    params.lineHeight,
    params.palette,
    params.bgPalette,
  ]);

  return (
    <canvas
      ref={canvasRef}
      className="canvas-stipple"
      data-testid="canvas-halftone"
      aria-hidden="true"
    />
  );
}
